import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { VerqoLockup } from "../components/VerqoLockup";
import { postLoginUrl } from "../lib/hostScope";
import {
  ArrowUpRight, ArrowRight, Banknote, ShieldCheck, Zap, Layers,
  CreditCard, Truck, Sparkles, LineChart,
} from "lucide-react";
import LegalFooter from "../components/LegalFooter";

const ease = [0.22, 1, 0.36, 1];

// ── Persona-specific marketing copy ──────────────────────────────────────
const COPY = {
  seller: {
    eyebrow: "For Brands & Suppliers",
    headline: "Sell more.\nGet paid in 24h.",
    sub: "Buyers take Net-60. You take cash tomorrow. We carry every dollar of risk in between.",
    proofs: [
      { icon: Banknote, label: "D+1 payouts on every invoice" },
      { icon: ShieldCheck, label: "Zero bad debt — we underwrite the buyer" },
      { icon: Zap, label: "3× checkout conversion with embedded terms" },
      { icon: Layers, label: "One pipe to Shopify, NetSuite, QuickBooks" },
    ],
    quote: "We replaced four tools, killed our collections team, and grew GMV 38% in a quarter.",
    quoteAuthor: "VP Wholesale, Glow Cosmetics",
  },
  buyer: {
    eyebrow: "For Wholesale Buyers",
    headline: "Buy now.\nPay in 60.",
    sub: "Operating capital on tap. AI-decisioned credit in seconds. Zero paperwork, zero interest games.",
    proofs: [
      { icon: CreditCard, label: "Net-30/60/90 — instant decision" },
      { icon: Sparkles, label: "One login. Every supplier you order from" },
      { icon: Truck, label: "Insured shipping baked into checkout" },
      { icon: LineChart, label: "Live cash position across every PO" },
    ],
    quote: "Approved for Net-60 in 90 seconds. We doubled our order book without touching a bank.",
    quoteAuthor: "Founder, Apex Goods",
  },
};

export default function Login() {
  const { login, logout, verifySmsLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialPersona = params.get("as") === "seller" ? "seller" : "buyer";
  const [persona, setPersona] = useState(initialPersona);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [smsMasked, setSmsMasked] = useState("");
  const [smsChallengeToken, setSmsChallengeToken] = useState(null);
  const [needsSmsMfa, setNeedsSmsMfa] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const copy = useMemo(() => COPY[persona], [persona]);

  // Single place to perform the post-login redirect. `postLoginUrl` returns
  // either a relative path (dev/preview) or an absolute URL (prod —
  // buyers.verqo.com / sellers.verqo.com / ops.verqo.com). Absolute URLs must
  // use a full page navigation so the browser drops the right `.verqo.com`
  // cookie on the destination subdomain.
  const goToPortal = (role) => {
    const nextPath = params.get("next");
    const url = postLoginUrl(role, { nextPath });
    if (url.startsWith("http://") || url.startsWith("https://")) {
      window.location.href = url;
    } else {
      nav(url);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      // SMS-MFA second step
      if (needsSmsMfa) {
        const u = await verifySmsLogin(email, password, smsCode, smsChallengeToken);
        // Partner roles get auto-redirected to their portal (banking/lender/insurance/carrier).
        const partnerPortals = {
          banking_partner: "/banking", lender_partner: "/lender",
          insurance_partner: "/insurance", carrier_partner: "/carrier",
        };
        if (partnerPortals[u.role]) {
          toast.success(`Welcome back, ${u.name}`);
          goToPortal(u.role);
          return;
        }
        // Cross-role team-membership escape hatch (see comment further down)
        const hasCrossRoleMembership = (u.team_memberships || []).some(
          (m) => m.owner_role === persona && m.status === "active",
        );
        if (u.role !== "admin" && u.role !== persona && !hasCrossRoleMembership) {
          await logout();
          const wrong = u.role === "seller" ? "a Seller account" : "a Buyer account";
          const right = u.role === "seller" ? "I'm a Seller" : "I'm a Buyer";
          setErr(`This is ${wrong}. Switch to "${right}" above to continue.`);
          return;
        }
        toast.success(`Welcome back, ${u.name}`);
        goToPortal(hasCrossRoleMembership && u.role !== persona ? persona : u.role);
        return;
      }
      const u = await login(email, password, needsTotp ? totpCode : undefined);
      // TOTP-required challenge — any user with an authenticator enrolled
      // (admin or otherwise) needs to enter their 6-digit code before
      // we get a real session.
      if (u && u.requires_totp) {
        setNeedsTotp(true);
        toast.info("Enter your 6-digit authenticator code (or a backup code)");
        return;
      }
      // SMS-MFA challenge — backend asked for an SMS code instead of a token.
      // login() in AuthContext has ALREADY fired /sms/login/challenge for us,
      // so the SMS is already on its way. We just stash the challenge_token
      // here and show the SMS input; clicking Verify uses that token and
      // does NOT fire a second SMS.
      if (u.requires_sms_mfa) {
        setNeedsSmsMfa(true);
        setSmsMasked(u.masked || u.masked_phone || "your phone");
        setSmsChallengeToken(u.challenge_token || null);
        toast.info(`Code sent to ${u.masked || u.masked_phone || "your phone"}`);
        return;
      }
      // Partner roles auto-redirect to their portal — they don't fit
      // buyer/seller persona toggle so we bypass it.
      const partnerPortals = {
        banking_partner: "/banking", lender_partner: "/lender",
        insurance_partner: "/insurance", carrier_partner: "/carrier",
      };
      if (partnerPortals[u.role]) {
        toast.success(`Welcome back, ${u.name}`);
        goToPortal(u.role);
        return;
      }
      // Enforce persona match: a buyer who picks "I'm a Seller" (or
      // vice-versa) must be rejected at the UI boundary. Admins always
      // bypass since they don't fit either persona.
      // CROSS-ROLE EXCEPTION (Feb 2026): if the user has been invited
      // as a team member at a tenant whose owner has the OTHER role,
      // we let them in — they'll land on that seller's portal via the
      // team-membership escape hatch in RoleRoute.
      const hasCrossRoleMembership = (u.team_memberships || []).some(
        (m) => m.owner_role === persona && m.status === "active",
      );
      if (u.role !== "admin" && u.role !== persona && !hasCrossRoleMembership) {
        await logout();
        const wrong = u.role === "seller" ? "a Seller account" : "a Buyer account";
        const right = u.role === "seller" ? "I'm a Seller" : "I'm a Buyer";
        setErr(`This is ${wrong}. Switch to "${right}" above to continue.`);
        return;
      }
      toast.success(`Welcome back, ${u.name}`);
      if (u.role === "admin" && u.requires_2fa_setup) {
        toast.info("Please set up two-factor authentication to secure your admin access.", { duration: 6000 });
      }
      // Route to the portal that matches the persona they clicked. For a
      // cross-role team member (e.g. buyer accessing seller portal), use
      // `persona` rather than `u.role` so they land on the right side.
      goToPortal(hasCrossRoleMembership && u.role !== persona ? persona : u.role);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      // If the backend asks for a TOTP code, reveal the field instead of
      // showing an error.
      if (typeof detail === "string" && detail.toLowerCase().includes("admin 2fa required")) {
        setNeedsTotp(true);
        setErr("");
      } else {
        setErr(formatErr(detail) || e.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* LEFT — form */}
      <div className="p-8 lg:p-14 flex flex-col">
        <Link to="/" className="flex items-center" data-testid="login-back">
          <VerqoLockup size="md" />
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            {/* Persona toggle */}
            <div
              className="inline-flex p-1 rounded-full border border-foreground/15 bg-white/70 backdrop-blur text-[12px] mono uppercase tracking-[0.18em]"
              data-testid="login-persona-toggle"
            >
              <button
                type="button"
                onClick={() => setPersona("buyer")}
                data-testid="login-persona-buyer"
                className={`px-3.5 py-1.5 rounded-full transition-all ${persona === "buyer" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}
              >
                I'm a Buyer
              </button>
              <button
                type="button"
                onClick={() => setPersona("seller")}
                data-testid="login-persona-seller"
                className={`px-3.5 py-1.5 rounded-full transition-all ${persona === "seller" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}
              >
                I'm a Seller
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={persona}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35, ease }}
              >
                <h1 className="display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight">
                  {persona === "seller" ? "Welcome back, operator." : "Welcome back, builder."}
                </h1>
                <p className="text-foreground/60 mt-3 text-[15px]">
                  {persona === "seller"
                    ? "Your payouts, your buyers, your books — one place."
                    : "Your credit, your suppliers, your cashflow — one place."}
                </p>
              </motion.div>
            </AnimatePresence>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="email" className="text-[13px] text-foreground/70">Work email</Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1.5 h-11 rounded-lg border-foreground/15 bg-white"
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-[13px] text-foreground/70">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="mt-1.5 h-11 rounded-lg border-foreground/15 bg-white"
                  required
                />
              </div>
              {needsTotp && (
                <div data-testid="login-totp-wrap">
                  <Label htmlFor="totp" className="text-[13px] text-foreground/70">Authenticator code</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    data-testid="login-totp"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-1.5 h-11 rounded-lg border-foreground/15 bg-white tracking-[0.4em] text-center"
                    placeholder="123 456"
                    maxLength={6}
                    required
                  />
                  <p className="text-[11.5px] text-foreground/55 mt-1.5">Open your authenticator app and enter the 6-digit code for verqo.</p>
                </div>
              )}
              {needsSmsMfa && (
                <div data-testid="login-sms-wrap">
                  <Label htmlFor="sms" className="text-[13px] text-foreground/70">SMS verification code</Label>
                  <Input
                    id="sms"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    data-testid="login-sms"
                    value={smsCode}
                    onChange={e => setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-1.5 h-11 rounded-lg border-foreground/15 bg-white tracking-[0.4em] text-center"
                    placeholder="123 456"
                    maxLength={6}
                    autoFocus
                    required
                  />
                  <p className="text-[11.5px] text-foreground/55 mt-1.5">
                    Code sent to <span className="font-medium text-foreground">{smsMasked}</span>. It expires in 5 minutes.
                  </p>
                </div>
              )}
              {err && <div className="text-sm text-destructive" data-testid="login-error">{err}</div>}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 text-[14px] font-medium"
              >
                {loading ? "Signing in…" : needsSmsMfa ? <>Verify &amp; sign in <ArrowUpRight className="w-4 h-4 ml-1"/></> : <>Sign in <ArrowUpRight className="w-4 h-4 ml-1"/></>}
              </Button>
              <div className="text-right">
                <Link
                  to="/auth/forgot-password"
                  data-testid="login-forgot-password"
                  className="text-[12.5px] text-foreground/60 hover:text-foreground underline underline-offset-4 decoration-foreground/20 hover:decoration-foreground"
                >
                  Forgot password?
                </Link>
              </div>
            </form>

            {/* Google SSO · alternative auth (Feb 2026 — native verqo OAuth)
                We hit our /auth/google/authorize endpoint to mint a CSRF
                state token + receive Google's official authorize URL,
                then redirect the browser to accounts.google.com directly.
                User sees the verqo-branded consent screen (logo, name)
                — no third-party reference. After approval Google bounces
                them back to /auth/callback?code=…&state=… which the
                GoogleAuthCallback page exchanges for a verqo JWT. */}
            {!needsSmsMfa && !needsTotp && (
              <>
                <div className="flex items-center gap-3 mt-6 text-[11.5px] text-foreground/45 mono uppercase tracking-[0.18em]">
                  <div className="flex-1 h-px bg-foreground/10"/>or<div className="flex-1 h-px bg-foreground/10"/>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/auth/google/authorize");
                      if (data?.authorize_url) {
                        window.location.href = data.authorize_url;
                      } else {
                        toast.error("Google sign-in is unavailable right now. Try again in a moment.");
                      }
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      const msg = detail?.message || (typeof detail === "string" ? detail : null) || "Google sign-in failed to start. Please try again.";
                      toast.error(msg);
                    }
                  }}
                  data-testid="login-google"
                  className="mt-4 w-full h-11 rounded-full border border-border bg-background hover:bg-secondary/60 transition-colors flex items-center justify-center gap-2.5 text-[14px] font-medium"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                  </svg>
                  Continue with Google
                </button>
              </>
            )}

            <div className="mt-6 text-sm text-foreground/70">
              No account?{" "}
              <Link
                to={`/register?as=${persona}`}
                className="underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
                data-testid="login-to-register"
              >
                {persona === "seller" ? "Start selling — free" : "Get instant credit"}
              </Link>
            </div>

          </div>
        </div>

        <LegalFooter />
      </div>

      {/* RIGHT — persona panel */}
      <div className="hidden lg:block relative bg-foreground text-background overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-[0.07]"/>
        <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-accent/30 blur-3xl"/>
        <div className="absolute -left-20 top-1/3 w-72 h-72 rounded-full bg-accent/10 blur-3xl"/>

        <div className="relative p-14 xl:p-16 h-full flex flex-col justify-between">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-accent/90 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_currentColor]"/>
            {copy.eyebrow}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={persona}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease }}
            >
              <div className="display text-5xl xl:text-6xl leading-[1.02] tracking-tight whitespace-pre-line">
                {copy.headline}
              </div>
              <p className="text-background/65 mt-6 max-w-md text-[15px] leading-relaxed">{copy.sub}</p>

              <ul className="mt-10 space-y-3.5 max-w-md">
                {copy.proofs.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.li
                      key={p.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease }}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-0.5 w-7 h-7 rounded-md bg-accent/15 ring-1 ring-accent/30 grid place-items-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-accent"/>
                      </span>
                      <span className="text-[14.5px] text-background/85">{p.label}</span>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={`q-${persona}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="border-t border-background/15 pt-6 max-w-md"
            >
              <p className="text-[15px] text-background/85 italic leading-relaxed">"{copy.quote}"</p>
              <p className="mt-2 text-[12px] mono uppercase tracking-[0.18em] text-background/55">{copy.quoteAuthor}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
